import React from 'react'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import { ConfirmationDialog } from '../../../legacy-design-system/modal'
import { FormattedMessage } from 'react-intl'
import gql from 'graphql-tag'
import { Mutation, FetchResult } from 'react-apollo'
import { PureQueryOptions } from 'apollo-client'
import { logError, logSuccess } from '../../toaster'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'

interface RestoreDialogProps extends RenderDialogProps {
  readonly applicationID?: string
}

export function ConfirmRestoreAppDialog({
  isOpen,
  closeDialog,
  applicationID,
  restore
}: RestoreDialogProps & {
  readonly restore: () => Promise<FetchResult<any> | void>
}) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onCancel={closeDialog}
      onConfirm={() => {
        closeDialog()
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        restore()
      }}
      renderConfirmMessage={() => <FormattedMessage id="action_unarchiveApp" />}
      renderCancelMessage={() => <FormattedMessage id="action_cancel" />}
      renderTitle={() => (
        <FormattedMessage
          id="plural_applications_unarchiveApps"
          values={{
            count: applicationID ? 1 : 2
          }}
        />
      )}
      renderBody={() => (
        <FormattedMessage
          id="plural_applications_restoreApplicationsConfirmation"
          values={{
            count: applicationID ? 1 : 2
          }}
        />
      )}
      size="small"
    />
  )
}

class RestoreMutation extends Mutation<
  any,
  {
    readonly applicationID: string
    readonly organizationID: string
  }
> {}

const restoreApplication = gql`
  mutation RestoreApplication($organizationID: ID!, $applicationID: ID!) {
    restoreApplication(
      input: { organizationID: $organizationID, id: $applicationID }
    ) {
      id
      name
    }
  }
`

interface MutationProps {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

export function RestoreDialog({
  applicationID,
  organizationID,
  refetchQueries,
  refresh,
  ...props
}: RestoreDialogProps & MutationProps & { readonly applicationID: string }) {
  return (
    <RestoreMutation mutation={restoreApplication}>
      {(restoreApplication) => (
        <ContrastFormattedMessage
          id="plural_applications_successfullyUnarchivedApplication"
          values={{ count: 1 }}
          children={(successMessage) => (
            <ContrastFormattedMessage
              id="plural_applications_errorUnarchivingApplication"
              values={{ count: 1 }}
              children={(errorMessage) => (
                <ConfirmRestoreAppDialog
                  {...props}
                  applicationID={applicationID}
                  restore={() => {
                    return restoreApplication({
                      refetchQueries,
                      variables: { applicationID, organizationID },
                      optimisticResponse: {
                        restoreApplication: {
                          id: applicationID,
                          name: '',
                          __typename: 'Application'
                        },
                        __typename: 'Mutation'
                      }
                    })
                      .then(() => {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        refresh()
                        logSuccess(successMessage)
                      })
                      .catch((error) => {
                        logError(errorMessage)
                        return error
                      })
                  }}
                />
              )}
            />
          )}
        />
      )}
    </RestoreMutation>
  )
}

class BulkRestoreMutation extends Mutation<
  any,
  {
    readonly organizationID: string
    readonly applicationIDs: string[]
  }
> {}

const restoreApplications = gql`
  mutation RestoreApplications($organizationID: ID!, $applicationIDs: [ID!]!) {
    restoreApplications(
      input: { organizationID: $organizationID, ids: $applicationIDs }
    ) {
      _placeholder
    }
  }
`

interface BulkProps {
  readonly applicationIDs: string[]
}

export function BulkRestoreDialog({
  applicationIDs,
  organizationID,
  refetchQueries,
  refresh,
  ...props
}: RestoreDialogProps & MutationProps & BulkProps) {
  return (
    <BulkRestoreMutation mutation={restoreApplications}>
      {(restoreApplications) => {
        return (
          <ContrastFormattedMessage
            id="plural_applications_successfullyUnarchivedApplication"
            values={{ count: applicationIDs.length }}
            children={(successMessage) => (
              <ContrastFormattedMessage
                id="plural_applications_errorUnarchivingApplication"
                values={{ count: applicationIDs.length }}
                children={(errorMessage) => (
                  <ConfirmRestoreAppDialog
                    {...props}
                    restore={() => {
                      return restoreApplications({
                        refetchQueries,
                        variables: { applicationIDs, organizationID },
                        optimisticResponse: {
                          restoreApplications: {
                            _placeholder: true,
                            __typename: 'ApplicationBulkResponse'
                          },
                          __typename: 'Mutation'
                        }
                      })
                        .then(() => {
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          refresh()
                          logSuccess(successMessage)
                        })
                        .catch((error) => {
                          logError(errorMessage)
                          return error
                        })
                    }}
                  />
                )}
              />
            )}
          />
        )
      }}
    </BulkRestoreMutation>
  )
}

class BulkRestoreByFilterMutation extends Mutation<
  any,
  {
    readonly excludedApplications: string[]
    readonly filters: any
    readonly organizationID: string
  }
> {}

const restoreApplicationsByFilters = gql`
  mutation RestoreApplicationsByFilters(
    $organizationID: ID!
    $excludedApplications: [ID!]!
    $filters: ApplicationFilterArg
  ) {
    restoreApplicationsByFilters(
      input: {
        organizationID: $organizationID
        excludedApplications: $excludedApplications
        filters: $filters
      }
    ) {
      _placeholder
    }
  }
`

interface BulkWithFiltersProps {
  readonly excludedApplications: string[]
  readonly filters: any
}

export function BulkRestoreWithFiltersDialog({
  excludedApplications,
  organizationID,
  filters,
  refetchQueries,
  refresh,
  ...props
}: RestoreDialogProps & MutationProps & BulkWithFiltersProps) {
  return (
    <BulkRestoreByFilterMutation mutation={restoreApplicationsByFilters}>
      {(restoreApplicationsByFilters) => {
        return (
          <ConfirmRestoreAppDialog
            {...props}
            restore={() => {
              return restoreApplicationsByFilters({
                refetchQueries,
                variables: { excludedApplications, organizationID, filters },
                optimisticResponse: {
                  restoreApplicationsByFilters: {
                    _placeholder: true,
                    __typename: 'ApplicationBulkResponse'
                  },
                  __typename: 'Mutation'
                }
              }).then(refresh)
            }}
          />
        )
      }}
    </BulkRestoreByFilterMutation>
  )
}
